import React from "react";

import BodyPortal from "@components/General/BodyPortal";

import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import type { QontactPointInformation } from "@models/QontactPoint";
import { HollowedButton, IconButton, NormalButton } from "@shared/designSystem/Button";
import { HorizontalContainer, SimpleCardContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { Modal } from "@shared/designSystem/messages/Modal";
import { SystemIcon } from "@shared/designSystem/SystemIcon";
import { SmallText, Text } from "@shared/designSystem/Texts";
import { H3Title } from "@shared/designSystem/Titles";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { formatShiftDays, formatShiftHeadcount, formatShiftHours } from "@utils/ShiftRecipeFormat";
import ShiftRecipeModal from "./ShiftRecipeModal";
import { useQontactPointTurnosTabViewModel } from "./QontactPointTurnosTabViewModel";

interface QontactPointTurnosTabProps {
    orgID: string;
    qontactPoint: QontactPointInformation;
}

/** Turnos tab of the qontact point detail (design 2b) */
const QontactPointTurnosTab: React.FC<QontactPointTurnosTabProps> = ({ orgID, qontactPoint }) => {
    const viewModel = useQontactPointTurnosTabViewModel(orgID, qontactPoint);

    const addButton = viewModel.canManage ? (
        <NormalButton text={"Agregar turno".localized()} onClick={viewModel.openNew} width="180px" />
    ) : null;

    const content = viewModel.loading ? (
        <QontyLoading text={"Cargando turnos…".localized()} withQonty={true} darkMode={false} />
    ) : viewModel.loadError ? (
        <VerticalContainer width="100%" alignment="center" gap={designSystemStyles.gapMd} margin="0">
            <QontyEmptyState text={"No se pudieron cargar los turnos".localized()} darkMode={false} fullSize={false} />
            <HollowedButton text={"Reintentar".localized()} onClick={viewModel.reload} width="160px" />
        </VerticalContainer>
    ) : viewModel.recipes.length === 0 ? (
        <VerticalContainer width="100%" alignment="center" gap={designSystemStyles.gapMd} margin="0">
            <QontyEmptyState text={"Este Qontact Point todavía no tiene turnos".localized()} darkMode={false} fullSize={false} />
            <SmallText
                text={"Agregá un turno para saber quién tiene que fichar y cuándo.".localized()}
                color={designSystemStyles.colorTextSecondary}
            />
            {addButton}
        </VerticalContainer>
    ) : (
        <VerticalContainer width="100%" minHeight="0" scrollable={true} gap={designSystemStyles.gapSm} margin="0">
            {viewModel.recipes.map((recipe) => (
                <SimpleCardContainer key={recipe.firestoreRecordId ?? recipe.name} variant="inlineSetup">
                    <HorizontalContainer width="100%" gap={designSystemStyles.gapMd}>
                        <SystemIcon type="clock" color={designSystemStyles.colorText} />
                        <VerticalContainer width="100%" minWidth="0" gap={designSystemStyles.gapXxxSm} margin="0">
                            <Text text={`**${(recipe.name ?? "").trim()}**`} markdown={true} truncate={true} />
                            <SmallText
                                text={[formatShiftDays(recipe), formatShiftHours(recipe), formatShiftHeadcount(recipe)].join(" · ")}
                                color={designSystemStyles.colorTextSecondary}
                            />
                        </VerticalContainer>
                        {viewModel.canManage && (
                            <HorizontalContainer width="auto" gap={designSystemStyles.gapSm}>
                                <IconButton iconType="edit" tooltip={"Editar turno".localized()} onClick={() => viewModel.openEdit(recipe)} />
                                <IconButton iconType="delete" tooltip={"Eliminar turno".localized()} onClick={() => viewModel.askDelete(recipe)} />
                            </HorizontalContainer>
                        )}
                    </HorizontalContainer>
                </SimpleCardContainer>
            ))}
        </VerticalContainer>
    );

    return (
        // Fills the tab card (fixed height): header stays, the shifts list scrolls inside
        <VerticalContainer width="100%" height="100%" minHeight="0" scrollable={true} gap={designSystemStyles.gapLg} padding={designSystemStyles.paddingLg} margin="0">
            <HorizontalContainer width="100%" justifyContent="space-between" flexWrap="wrap" gap={designSystemStyles.gapSm}>
                <H3Title text={"Turnos".localized()} />
                {/* Nothing to export (or add from here) while there are no shifts: the empty state has its own button */}
                {viewModel.recipes.length > 0 && (
                    <HorizontalContainer width="auto" gap={designSystemStyles.gapSm}>
                        <HollowedButton
                            text={"Exportar turnos".localized()}
                            onClick={viewModel.exportShifts}
                            disabled={viewModel.loading || viewModel.isExporting}
                            loading={viewModel.isExporting}
                            width="180px"
                        />
                        {addButton}
                    </HorizontalContainer>
                )}
            </HorizontalContainer>

            {content}

            <BodyPortal>
            {viewModel.modalTarget !== null && (
                <ShiftRecipeModal
                    orgID={orgID}
                    qontactPoint={qontactPoint}
                    recipe={viewModel.modalTarget === "new" ? undefined : viewModel.modalTarget}
                    collaborators={viewModel.collaborators}
                    onClose={viewModel.closeModal}
                    onSaved={viewModel.onSaved}
                />
            )}

            <Modal
                isOpen={viewModel.recipeToDelete !== null}
                onClose={viewModel.cancelDelete}
                title={"¿Eliminar este turno?".localized()}
                width="480px"
                maxWidth="94vw"
            >
                <VerticalContainer width="100%" gap={designSystemStyles.gapLg} margin="0">
                    <Text
                        text={"Se eliminará **%s**. Los fichajes ya registrados se conservan.".localized(viewModel.recipeToDelete?.name ?? "")}
                        markdown={true}
                    />
                    <HorizontalContainer width="100%" justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                        <HollowedButton text={"Cancelar".localized()} onClick={viewModel.cancelDelete} width="140px" disabled={viewModel.isDeleting} />
                        <NormalButton
                            text={"Eliminar".localized()}
                            onClick={viewModel.confirmDelete}
                            width="140px"
                            loading={viewModel.isDeleting}
                            disabled={viewModel.isDeleting}
                        />
                    </HorizontalContainer>
                </VerticalContainer>
            </Modal>
            </BodyPortal>
        </VerticalContainer>
    );
};

export default QontactPointTurnosTab;
